import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo, faqList } from '@/mocks/about';
import { categories } from '@/mocks/videos';

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        payload.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q1fdknlsngrm4llq4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const pageTitle = '关于我们 - 爱液视频官网';
  const pageDescription = '了解爱液视频品牌故事、发展历程、核心价值及联系方式。爱液视频是领先的正版高清视频平台，注册用户超过5000万。';

  return (
    <>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="爱液视频关于我们,爱液视频品牌介绍,爱液视频联系方式,爱液视频官网"
        canonicalUrl="https://www.aiyevideo.com/about"
      />

      <header>
        <Navbar />
      </header>

      <main className="pt-16">
        {/* Hero */}
        <section className="relative h-48 md:h-64 flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20background%20with%20soft%20amber%20gradient%2C%20smooth%20flowing%20shapes%2C%20minimalist%20corporate%20style%2C%20professional%20and%20clean%20composition&width=1400&height=400&seq=about-hero&orientation=landscape"
            alt="关于爱液视频"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">关于我们</h1>
            <p className="text-sm text-background-50/80 mt-2">了解爱液视频的品牌故事与发展历程</p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Introduction */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                    <i className="ri-play-fill text-background-50 text-xl"></i>
                  </div>
                  <span className="font-heading text-xl font-bold text-foreground-950">
                    {aboutInfo.brand.name}
                  </span>
                </div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  {aboutInfo.brand.slogan}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  {aboutInfo.brand.description}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  <strong>使命：</strong>{aboutInfo.brand.mission}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  <strong>愿景：</strong>{aboutInfo.brand.vision}
                </p>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20warm%20ambient%20lighting%2C%20creative%20team%20working%20environment%2C%20clean%20minimalist%20design%2C%20professional%20atmosphere%2C%20cinematic%20composition&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="爱液视频办公环境"
                  title="爱液视频 - 品牌文化"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">核心价值观</h2>
              <p className="text-sm text-foreground-500 mt-2">驱动爱液视频不断前行的核心信念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutInfo.brand.values.map((value) => (
                <div key={value.title} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-check-double-line text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">发展历程</h2>
              <p className="text-sm text-foreground-500 mt-2">自2018年成立以来的重要里程碑</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-6">
                {aboutInfo.timeline.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1.5 md:-translate-x-1.5 mt-1.5 z-10 ring-4 ring-background-50"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                      <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                        <span className="text-accent-600 font-heading font-bold text-lg">{item.year}</span>
                        <p className="text-sm text-foreground-600 mt-1">{item.event}</p>
                      </div>
                    </div>
                    {index % 2 === 1 && <div className="hidden md:block md:w-1/2"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">用户口碑</h2>
              <p className="text-sm text-foreground-500 mt-2">来自真实用户的评价与反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
              <p className="text-sm text-foreground-500 mt-2">如有任何问题或建议，欢迎随时联系我们</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服电话</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500 mt-0.5">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="bg-accent-50 border border-accent-200 rounded-lg p-8 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-accent-700 mb-2">消息已发送</h3>
                    <p className="text-sm text-accent-600">感谢您的留言，我们将在1-2个工作日内回复您。</p>
                    <button
                      className="mt-4 text-sm text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
                      onClick={() => setFormStatus('idle')}
                    >
                      发送新消息
                    </button>
                  </div>
                ) : formStatus === 'error' ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-100 text-red-600">
                      <i className="ri-close-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-red-700 mb-2">发送失败</h3>
                    <p className="text-sm text-red-600 mb-1">{formError || '请稍后重试或直接拨打客服电话'}</p>
                    <button
                      className="mt-4 text-sm text-red-600 hover:text-red-700 font-medium cursor-pointer"
                      onClick={() => setFormStatus('idle')}
                    >
                      重新发送
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form="">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm outline-none bg-background-100 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm outline-none bg-background-100 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg text-sm outline-none bg-background-100 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="spam-catcher">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">常见问题</h2>
            </div>
            <div className="space-y-3">
              {faqList.map((faq, index) => (
                <div key={index} className="border border-background-200/70 rounded-lg overflow-hidden bg-background-50">
                  <button
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-50 transition-colors"
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  >
                    <span className="font-medium text-foreground-900 text-sm pr-4">{faq.question}</span>
                    <i className={`${activeFaq === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 flex-shrink-0`}></i>
                  </button>
                  {activeFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">快速导航</h2>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}